import React, { useEffect, useState } from "react";
import ModalContentWithCloseButton from "../common/ModalContentWithCloseButton";
import FormikFormLine from "../formik-form-components/FormikFormLine";
import { Formik } from "formik";
import * as Yup from "yup";
import { EMAIL_SCHEMA } from "../../utils/forms";
import Button from "../common/Button";
import { ButtonTypes, ButtonVariants } from "../common/Button/constants";
import { Description, FinePrint, Form, Success } from "./EmailPopUpStyles";
import { useAppSelector } from "../../utils/hooks/store";
import {
  FORGOT_PASSWORD,
  INTERNAL_SIGNIN,
  MY_ACCOUNT,
  REVIEW_BOARD,
  SHOPPING_CART,
  UPLOAD_BOARD,
} from "../../constants/routes";
import { useRouter } from "next/router";
import {
  SignUpFormValues,
  useEmailMarketingSignUp,
} from "../../utils/hooks/emailMarketingSignUp";
import WhyRegister from "../Authentication/WhyRegister";
import {
  ACTION_QUERY_KEY,
  ACTION_QUERY_SIGN_UP,
} from "../Authentication/constants";
import { FieldTypes } from "../formik-form-components/FormikFormLine/constants";
import { track } from "@amplitude/analytics-browser";
import { useCookies } from "react-cookie";
import Modal from "../common/Modal";

const schema = Yup.object().shape({
  email: EMAIL_SCHEMA,
});

const DID_SEE_EMAIL_POP_UP = "didSeeEmailPopUp";

const RESTRICTED_ROUTES = new Set([
  REVIEW_BOARD,
  UPLOAD_BOARD,
  MY_ACCOUNT,
  SHOPPING_CART,
  INTERNAL_SIGNIN,
  FORGOT_PASSWORD,
]);

export default function EmailPopUp({ isMenuOpen }: { isMenuOpen: boolean }) {
  const [cookies, setCookie] = useCookies([DID_SEE_EMAIL_POP_UP], {});
  const router = useRouter();
  const [firstLoadTime] = useState(() => Date.now());
  const [time, setTime] = useState(() => Date.now());
  const [showPopUp, setShowPopUp] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const signedIn = useAppSelector((state) => state.auth.signedIn);
  const onSuccess = () => {
    setSubmitted(true);
  };
  const { signUp, errored, validationError, loading } = useEmailMarketingSignUp(
    { onSuccess },
  );

  const closePopUp = () => {
    setCookie(DID_SEE_EMAIL_POP_UP, true);
    setShowPopUp(false);
  };

  const dismissPopUp = () => {
    closePopUp();
    track("Pop up dismissed");
  };

  const uploadBoard = () => {
    closePopUp();
    void router.push(UPLOAD_BOARD);
  };

  const signUpForAccount = () => {
    closePopUp();
    void router.push({
      pathname: MY_ACCOUNT,
      query: { [ACTION_QUERY_KEY]: ACTION_QUERY_SIGN_UP },
    });
  };

  useEffect(() => {
    if (errored) {
      onSuccess();
    }
  }, [errored]);

  useEffect(() => {
    const interval = setInterval(() => setTime(Date.now()), 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const secondsSinceFirstLoad = (time - firstLoadTime) / 1000;
    const seen = !!cookies.didSeeEmailPopUp;
    if (
      secondsSinceFirstLoad > 10 &&
      !seen &&
      !signedIn &&
      !RESTRICTED_ROUTES.has(router.pathname) &&
      !isMenuOpen
    ) {
      setShowPopUp(true);
      track("Email pop up appeared");
    } else {
      setShowPopUp(false);
    }
  }, [
    cookies.didSeeEmailPopUp,
    firstLoadTime,
    isMenuOpen,
    router.pathname,
    signedIn,
    time,
  ]);

  const initialValues: SignUpFormValues = {
    email: "",
    source: "",
  };

  const renderEmailCollectionForm = () => {
    return (
      <>
        <Description>
          Sign up to get $100 off your first order over $200.
        </Description>
        <Formik
          initialValues={initialValues}
          onSubmit={signUp}
          validationSchema={schema}
        >
          {({ handleSubmit, isValid }) => (
            <Form onSubmit={handleSubmit}>
              <FormikFormLine
                name="email"
                label="Email"
                type={FieldTypes.email}
                errorMessage={validationError || undefined}
              />
              <Button
                label="Sign up"
                type={ButtonTypes.SUBMIT}
                disabled={!isValid || loading}
                centerText
              />
              <FinePrint>
                Valid on first-time email address submissions only.
              </FinePrint>
            </Form>
          )}
        </Formik>
      </>
    );
  };

  const renderSuccess = () => {
    return (
      <Success>
        <Description>Thanks for signing up!</Description>
        <Button
          label="Upload a board and save $100"
          type={ButtonTypes.SUBMIT}
          centerText
          onClick={uploadBoard}
          styleProps={{ marginTop: "1rem", marginBottom: "3rem" }}
        />
        <WhyRegister imperative />
        <Button
          centerText
          label="Create an account"
          variant={ButtonVariants.SECONDARY}
          onClick={signUpForAccount}
        />
      </Success>
    );
  };

  return (
    <Modal isOpen={showPopUp} onRequestClose={dismissPopUp}>
      <ModalContentWithCloseButton
        title="FlashPCB Newsletter"
        closeModal={dismissPopUp}
      >
        {submitted ? renderSuccess() : renderEmailCollectionForm()}
      </ModalContentWithCloseButton>
    </Modal>
  );
}
